/* eslint-disable @typescript-eslint/no-explicit-any */
import { message } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import Dragger from 'antd/es/upload/Dragger'
import {
  requestCreateRewardTum,
  requestDeleteRewardTum,
  requestGetRewardTumByTumId,
} from 'app/api/tradeUnionMembers'
import { IRewardTum, ITradeUnionMember } from 'app/api/tradeUnionMembers/model'
import R from 'app/assets/R'
import { SvgDeleteDesignIcon, SvgPictureIcon } from 'app/assets/svg-assets'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCol } from 'app/components/common/BaseCol'
import { BasePopconfirm } from 'app/components/common/BasePopconfirm'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { BaseInput } from 'app/components/common/inputs/BaseInput'
import {
  BaseModalComponent,
  IRefModal,
} from 'app/components/common/ModalComponent'
import { BaseDatePicker } from 'app/components/common/pickers/BaseDatePicker'
import { BaseTableManagement } from 'app/components/tables/BaseTableManagement'
import { RenderValueTable } from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { _DEV_ } from 'app/constant'
import { uploadMultipleFiles } from 'app/service/general/service'
import { Dayjs } from 'dayjs'
import { ResponseType } from 'parkway-web-common'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface IDataTable extends IRewardTum {
  key: number
}

export interface IFormCreateRewardTum {
  name: string
  year: Dayjs
  description?: string
  link: string
  file?: string
}

export const OtherRewardLayout = ({ tum }: { tum?: ITradeUnionMember }) => {
  const { t } = useTranslation()
  const [form] = BaseForm.useForm<IFormCreateRewardTum>()
  const modalCreateRef = useRef<IRefModal>(null)
  const [data, setData] = useState<IRewardTum[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoadingCreate, setIsLoadingCreate] = useState<boolean>(false)
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const [fileName, setFileName] = useState<string | undefined>(undefined)
  const [fileUpload, setFile] = useState<File | undefined>(undefined)

  // Xử lý sau khi upload thành công
  const handleUploadChange = (info: any) => {
    const file = info?.file
    if (file) {
      const url = URL.createObjectURL(file)
      setFileName(file?.name)
      form.setFieldsValue({ file: url })
      setFile(file)
    }
  }
  const getData = async () => {
    if (!tum?.id) {
      return
    }

    setIsLoading(true)
    try {
      const res: ResponseType<IRewardTum[]> = await requestGetRewardTumByTumId({
        id: tum?.id,
      })

      setData(res?.data ?? [])
    } catch (error) {
      _DEV_ && console.log('error', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getData()
  }, [tum?.id])

  const dataSource = useMemo(() => {
    return data?.map((item, index) => {
      return {
        key: index + 1,
        ...item,
      }
    })
  }, [data])

  const handleDelete = async (id?: number) => {
    if (!id) {
      return
    }

    setIsLoadingDelete(true)
    try {
      const res = await requestDeleteRewardTum({ id: id?.toString() })

      if (res) {
        getData()
        message.success('Xóa giải thưởng thành công')
      }
    } catch (error) {
      _DEV_ && console.log('error', error)
    } finally {
      setIsLoadingDelete(false)
    }
  }

  const handleCreateReward = async (values: IFormCreateRewardTum) => {
    if (!tum?.id || !fileUpload) {
      return
    }

    setIsLoadingCreate(true)
    try {
      const files = await uploadMultipleFiles([fileUpload])

      if (!files?.length) return

      // do something
      const res = await requestCreateRewardTum({
        link: files?.[0]?.url,
        linkName: files?.[0]?.name,
        year: values?.year?.format('YYYY'),
        name: values?.name,
        description: values?.description,
        trade_union_member_id: tum?.id,
      })

      if (res) {
        getData()
        message.success('Thêm giải thưởng thành công')
        modalCreateRef.current?.hide?.()
        form?.resetFields?.()
      }
    } catch (error) {
      _DEV_ && console.log('error', error)
    } finally {
      setIsLoadingCreate(false)
    }
  }

  const openModalCreate = () => {
    modalCreateRef.current?.open?.()
  }

  return (
    <BaseSpace>
      <BaseRow gutter={16} align={'middle'} justify={'space-between'}>
        <BaseCol>
          <BaseText
            children={'Giải thưởng khác'}
            fontWeight="semibold"
            fontSize="xs"
          />
        </BaseCol>

        <BaseCol>
          <BaseModalComponent
            titleModal="Thêm giải thưởng"
            buttonOpenModal={
              <BaseButton
                onClick={openModalCreate}
                children={'Thêm giải thưởng'}
              />
            }
            handleSubmit={form?.submit}
            ref={modalCreateRef}
            isLoadingConfirm={isLoadingCreate}
            renderContent={
              <BaseForm form={form} onFinish={handleCreateReward}>
                <BaseSpace size={0}>
                  <BaseForm.Item
                    required
                    label={'Tên giải'}
                    name={'name'}
                    rules={[
                      {
                        required: true,
                        message: 'Vui lòng nhập tên giải thưởng',
                      },
                    ]}
                  >
                    <BaseInput placeholder="Nhập tên giải" />
                  </BaseForm.Item>

                  <BaseForm.Item label={'Mô tả'} name={'description'}>
                    <TextArea
                      placeholder="Mô tả đơn vị tổ chức và thông tin ngắn gọn về giải thưởng"
                      maxLength={100}
                    />
                  </BaseForm.Item>

                  <BaseForm.Item
                    required
                    label={'Năm đạt giải'}
                    name={'year'}
                    rules={[
                      {
                        required: true,
                        message: 'Vui lòng chọn năm đạt giải',
                      },
                    ]}
                  >
                    <BaseDatePicker
                      picker="year"
                      placeholder="Chọn năm đạt giải"
                      format={'YYYY'}
                      style={{
                        width: '100%',
                      }}
                    />
                  </BaseForm.Item>

                  <BaseForm.Item
                    name={'image'}
                    valuePropName="file"
                    required
                    label={'Tài liệu đính kèm'}
                    rules={[
                      {
                        required: true,
                        message: t(R.strings.require_field, {
                          field: 'Image',
                        }),
                      },
                    ]}
                  >
                    <Dragger
                      name="file"
                      multiple={false}
                      onChange={handleUploadChange}
                      beforeUpload={() => false}
                      style={{
                        borderRadius: 10,
                        padding: '10px 20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        borderStyle: 'solid',
                      }}
                      showUploadList={false}
                    >
                      <BaseRow
                        gutter={16}
                        align={'middle'}
                        justify={'space-between'}
                      >
                        {fileName ? (
                          <BaseCol xl={8}>
                            <BaseText
                              fontWeight="semibold"
                              colorText="collapseBackgroundColor"
                              children={fileName}
                            />
                          </BaseCol>
                        ) : null}
                        <BaseCol xl={14}>
                          <p>
                            Drag an files here,{' '}
                            <span style={{ color: '#1890ff' }}>upload</span>, or{' '}
                            <span style={{ color: '#1890ff' }}>
                              browse files library
                            </span>
                            .
                          </p>
                        </BaseCol>

                        <BaseCol xl={2}>
                          <SvgPictureIcon />
                        </BaseCol>
                      </BaseRow>
                    </Dragger>
                  </BaseForm.Item>
                </BaseSpace>
              </BaseForm>
            }
          />
        </BaseCol>
      </BaseRow>

      <BaseTableManagement
        dataSource={dataSource}
        loading={isLoading || isLoadingDelete}
        columns={[
          convertColumnTable<IDataTable>({
            key: 'key',
            title: 'STT',
            fixed: 'left',
            classNameWidthColumnOverwrite: 'number-column',
            render: text => {
              return (
                <BaseText children={text} fontWeight="medium" opacity="0.7" />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            key: 'name',
            title: 'Tên giải',
            fixed: 'left',
            classNameWidthColumnOverwrite: 'very-big-column',
            render: text => {
              return <BaseText children={text} fontWeight="semibold" />
            },
          }),
          convertColumnTable<IDataTable>({
            key: 'description',
            title: 'Mô tả',
            render: text => {
              return (
                <BaseText children={text} fontWeight="medium" opacity="0.7" />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            key: 'year',
            title: 'Năm đạt giải',
            render: text => {
              return (
                <BaseText children={text} fontWeight="medium" opacity="0.7" />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            key: 'link',
            title: 'Tài liệu đính kèm',
            classNameWidthColumnOverwrite: 'very-big-column',
            render: (_, record) => {
              return (
                <RenderValueTable
                  value={record?.link_name}
                  type="Link"
                  onClick={() =>
                    window.open(record?.link, '_blank', 'noopener,noreferrer')
                  }
                />
              )
            },
          }),
          convertColumnTable<IDataTable>({
            key: 'key',
            title: '',
            classNameWidthColumnOverwrite: 'small-column',
            fixed: 'right',
            render: (_, record) => {
              return (
                <BasePopconfirm
                  title="Bạn có thực sự muốn xoá giải thưởng này?"
                  onConfirm={() => handleDelete(record?.id)}
                  children={
                    <BaseButton
                      icon={<SvgDeleteDesignIcon />}
                      type="text"
                    />
                  }
                />
              )
            },
          }),
        ]}
        className="row-overwrite-style"
        scroll={{ x: true }}
        pagination={{
          pageSize: 5,
        }}
      />
    </BaseSpace>
  )
}
